import { useQuery } from "@tanstack/react-query";
import { PageHeader, Badge } from "@/components/ui/PageHeader";
import { api } from "@/api/client";

/**
 * Gamification lab — points, badges, your own rank, and the community
 * boards. The people leaderboard was retired by the re-architecture.
 */

export default function GamificationLab() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Gamification"
        subtitle="Points, badges, your own rank, community boards — status without a scoreboard of people"
        breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Gamification" }]}
      />
      <MyStats />
      <PointsHistory />
      <CommunityBoards />
    </div>
  );
}

function MyStats() {
  const { data: stats } = useQuery({
    queryKey: ["gami-stats"],
    queryFn: () => api.get<{ success: true; data: Record<string, unknown> }>("/gamification/stats/me"),
  });
  const { data: rank } = useQuery({
    queryKey: ["gami-rank"],
    queryFn: () => api.get<{ success: true; data: Record<string, unknown> }>("/gamification/rank/me"),
  });
  const { data: badges } = useQuery({
    queryKey: ["gami-badges"],
    // `{ badges: [...] }`, the list the Parichay counts (R23). Read as a bare
    // array before, so this count and list never showed.
    queryFn: () =>
      api.get<{
        success: true;
        data: { badges: { code: string; name: string; awardedAt: string | null }[] };
      }>("/gamification/badges/me"),
  });

  const s = stats?.data;
  const myBadges = badges?.data?.badges ?? [];

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5">
      <h2 className="font-semibold mb-3">My standing</h2>
      <div className="grid grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-xs text-gray-500">Points</div>
          <div className="text-2xl font-semibold">{String(s?.totalPoints ?? "—")}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Rank</div>
          <div className="text-2xl font-semibold">{String((rank?.data as Record<string, unknown>)?.rank ?? "—")}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500">Badges</div>
          <div className="text-2xl font-semibold">{myBadges.length}</div>
        </div>
      </div>
      {myBadges.length > 0 && (
        <div className="flex gap-2 flex-wrap mt-3">
          {myBadges.map((b) => (
            <Badge key={b.code} variant="green">
              {b.name}
              {b.awardedAt ? ` · ${new Date(b.awardedAt).toLocaleDateString()}` : " · undated"}
            </Badge>
          ))}
        </div>
      )}
    </section>
  );
}

function PointsHistory() {
  const { data } = useQuery({
    queryKey: ["gami-history"],
    queryFn: () =>
      api.get<{ success: true; data: { transactions: Array<Record<string, unknown>> } }>(
        "/gamification/points/history",
        { limit: 15 },
      ),
  });
  const txs = data?.data?.transactions ?? [];

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5">
      <h2 className="font-semibold mb-3">Recent points</h2>
      {txs.length === 0 && <p className="text-sm text-gray-500">No point activity yet — vouch, ask, answer, RSVP…</p>}
      <ul className="space-y-1">
        {txs.map((t, i) => (
          <li key={i} className="text-sm text-gray-700 flex justify-between">
            <span>
              {String(t.actionType)}
              {t.description ? ` — ${String(t.description)}` : ""}
            </span>
            <span className="font-medium">+{String(t.points)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CommunityBoards() {
  // The PEOPLE leaderboard and per-category top-ten were deleted by the
  // re-architecture (public rankings of people are forbidden). What survives
  // is the communities board — cohorts ranked per-capita, never a person.
  const { data } = useQuery({
    queryKey: ["gami-community-board"],
    queryFn: () =>
      api.get<{ success: true; data: { communities: Array<Record<string, unknown>> } }>(
        "/gamification/leaderboard/communities",
      ),
  });

  const rows = (data?.data?.communities ?? []) as Array<Record<string, unknown>>;

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5 space-y-3">
      <h2 className="font-semibold">Community boards</h2>
      <p className="text-xs text-gray-500">
        Cohorts, not people — the individual leaderboard was retired.
      </p>
      <ol className="space-y-1 list-decimal list-inside">
        {rows.map((r, i) => (
          <li key={i} className="text-sm text-gray-700">
            {String(r.name ?? r.code ?? "?")} —{" "}
            <span className="font-medium">{String(r.perCapitaPoints ?? r.points ?? 0)}</span>
          </li>
        ))}
        {rows.length === 0 && <p className="text-sm text-gray-500">No cohorts placed yet.</p>}
      </ol>
    </section>
  );
}
